import React from 'react';
import { getTitle } from '../../../Components/Formatting/Customer/Title';
import { getSubTitle } from '../../../Components/Formatting/Customer/Subtitle';
import Time from '../../../Components/Formatting/Time';
import { classNames } from '../../../Services/helpers';
import Services from '../../../Components/Formatting/Services';
import { useGrants } from '../../../Data/Business';
import Grant from '../../../Components/Formatting/Grant';
import { useSession } from '../../../Components/Session';
import Duration from '../../../Components/Formatting/Duration';
import { useCalendarContext } from '../Provider';
import { appointmentClasses } from '../../../Data/Calendar';

const DayAppointmentsView = ({day, appointments}) => {
    const {activeGrants}  = useGrants();
    const {hasFeature} = useSession();
    const {pxPerHour, dayWidth} = useCalendarContext();

    const showMultipleUsers = !!(hasFeature('ADDITIONAL_USERS') && activeGrants && appointments);

    return <div className="shrink-0 pr-5" style={{width: dayWidth}}>

        <div className="relative">
            <div className="absolute top-0 left-0 w-full h-full flex flex-row">
                {showMultipleUsers === true && activeGrants.map(grant => {
                    // Filter out any users with no appointments on this day
                    if (appointments.filter(app => app.assignedTo === grant.userId).length === 0) {
                        return null;
                    }
                    return <Appointments key={grant.id} {...{grant, appointments, pxPerHour}} />
                })}
                {showMultipleUsers === true && (() => {
                    // Filter out any users with no appointments on this day
                    const unassigned = appointments.filter(app => app.assignedTo === null)
                    if (unassigned.length === 0) {
                        return null;
                    }
                    return <Appointments key="unassigned" {...{appointments: unassigned, grant: null, pxPerHour}} />
                })()}
                {showMultipleUsers === false &&
                    <Appointments {...{grant: null, appointments, pxPerHour}} />
                }
            </div>
            <HourBars pxPerHour={pxPerHour} />
        </div>
    </div>;
}

const Appointments = ({grant, appointments, pxPerHour}) => {
    if (!appointments) {
        return null;
    }

    let lastEnd = 0, indent = 0;
    return <div className="relative h-full w-auto grow mx-1">
        {appointments.filter(app => grant === null || app.assignedTo === grant.userId).map((appointment) => {
            const top = timeToPx(appointment.estimatedStartTime, pxPerHour);
            const minHeight = timeToPx(appointment.duration, pxPerHour);
            indent = (lastEnd > top) ? indent += 50 : 0;
            lastEnd = top + minHeight;

            if (appointment.type === 'Start' || appointment.type === 'End') {
                return <StartEndCap key={appointment.appointmentId} {...{appointment, top, indent, grant}} />
            }

            if (appointment.type === 'Break') {
                return <Break key={appointment.appointmentId} {...{appointment, top, minHeight, indent}} />
            }

            return <Appointment key={appointment.appointmentId}
                {...{appointment, top, minHeight, indent}}
            />
        })}
    </div>
}

const Break = ({appointment, top, minHeight, indent}) => {
    return <div key={appointment.appointmentId}
        className={classNames(
            'absolute rounded-lg w-full overflow-hidden shadow-md py-0 px-2',
            'backdrop-blur-sm bg-slate-200/50 text-slate-600 border border-slate-300',
            'flex flex-row items-center'
        )}
        style={{
            top,
            height: minHeight,
            left: indent,
            width: `calc(100% - ${indent}px)`,
        }}
    >
        <span className="text-sm whitespace-nowrap">
            <strong className="w-[3rem] inline-block"><Time time={appointment.estimatedStartTime} /></strong>
            Break (<Duration time={appointment.duration} />)
        </span>
    </div>
}

const Appointment = ({appointment, top, minHeight, indent}) => {
    const {setPreviewingAppointment} = useCalendarContext();

    return <div key={appointment.appointmentId}
        onClick={() => setPreviewingAppointment(appointment)}
        className={classNames(
            'absolute rounded-lg w-full overflow-hidden shadow-md py-0 px-2',
            appointmentClasses[appointment.status],
        )}
        style={{
            top,
            height: minHeight,
            left: indent,
            width: `calc(100% - ${indent}px)`,
        }}
    >
        <span className="text-sm whitespace-nowrap">
            <strong className="w-[3rem] inline-block"><Time time={appointment.estimatedStartTime} /></strong>
            {getTitle(appointment.customer) ?? 'No customer name'}<br />
            {getSubTitle(appointment.customer)}<br />
            <Services services={appointment?.job?.services} />
        </span>
    </div>
}
const StartEndCap = ({appointment, top, indent, grant}) => {
    return <>
        {appointment.type === 'Start' && <div className="absolute -mt-10 w-full flex items-center justify-center" style={{top}}>
            {grant && <Grant grantId={grant.id} size="8" />}
        </div>}
        <div key={appointment.appointmentId}
            className="absolute rounded-lg w-full overflow-hidden shadow-md py-0 px-2 bg-slate-100/80 text-slate-600"
            style={{
                top,
                left: indent,
                width: `calc(100% - ${indent}px)`,
            }}
        >
            <strong className="w-[3.5rem] inline-block"><Time time={appointment.estimatedStartTime} /></strong>
            {appointment.type === 'Start' && 'Start'}
            {appointment.type === 'End' && 'Finish'}
        </div>
    </>
}

const HourBars = ({pxPerHour}) => {
    const hours = Array.from({length: 24}, (_, i) => i);

    return <div className="static top-0 left-0 w-full h-full">
        {hours.map((hour) => (
            <div key={hour} className="border-b border-gray-400" style={{height: pxPerHour}}>
                <span className="ml-2 text-sm text-gray-400">{hour}:00</span>
            </div>
        ))}
    </div>
}

const timeToPx = (time, pxPerHour) => {
    if (!time) {
        return null;
    }

    const [hour, minute] = time.split(':');

    return Math.round(
        (parseInt(hour) * pxPerHour) + (parseInt(minute) * pxPerHour / 60)
    );
}

export default DayAppointmentsView;
